import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'wouter';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import OnboardingShell from '@/components/OnboardingShell';
import {
  ActiveHoursDaysStep,
  ReminderFrequencyStep,
  DailyGoalStep,
} from '@/components/onboarding/SharedSteps';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { apiRequest } from '@/lib/queryClient';
import { getErrorMessage } from '@/lib/errors';
import { resolveRecommendedDailyTargetMl, resolveDailyTargetMl } from '@/lib/hydrationGoals';
import { toProfileGoals } from '@shared/hydrationGoals';

const DAY_MAP: Record<string, string> = {
  Mon: 'monday',
  Tue: 'tuesday',
  Wed: 'wednesday',
  Thu: 'thursday',
  Fri: 'friday',
  Sat: 'saturday',
  Sun: 'sunday',
};
const DAY_MAP_REV: Record<string, string> = Object.fromEntries(
  Object.entries(DAY_MAP).map(([k, v]) => [v, k])
);

/**
 * Hydration Settings — starts on Reminder Frequency (not Active Hours),
 * then Daily Goal, then Active Hours (per visuals QA: start from the next page).
 */
export default function HydrationSettings() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [step, setStep] = useState(0);
  const totalSteps = 3;

  const [age, setAge] = useState('');
  const [gender, setGender] = useState('female');
  const [activeDays, setActiveDays] = useState<string[]>(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('21:00');
  const [reminderInterval, setReminderInterval] = useState(45);
  const [dailyTarget, setDailyTarget] = useState('1500');
  const [saving, setSaving] = useState(false);

  const { data: profileData, isLoading } = useQuery({
    queryKey: ['/api/profile'],
    enabled: isAuthenticated,
    retry: false,
  });

  useEffect(() => {
    if (!profileData) return;
    const p = profileData as Record<string, unknown>;
    setAge(String(p.age || ''));
    setGender(String(p.gender || 'female'));
    setStartTime(String(p.activeHoursStart || '09:00'));
    setEndTime(String(p.activeHoursEnd || '21:00'));
    setReminderInterval(Number(p.reminderInterval) || 45);
    setDailyTarget(String(resolveDailyTargetMl(toProfileGoals(p))));
    const days = (p.activeDays as string[]) || [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
    ];
    setActiveDays(days.map((d) => DAY_MAP_REV[d] || d));
  }, [profileData]);

  const recommended = () =>
    resolveRecommendedDailyTargetMl({
      age: parseInt(age, 10) || undefined,
      gender,
      activeHoursStart: startTime,
      activeHoursEnd: endTime,
      reminderInterval,
    });

  const toggleDay = (d: string) =>
    setActiveDays((p) => (p.includes(d) ? p.filter((x) => x !== d) : [...p, d]));

  const close = () => setLocation('/profile');
  const back = () => (step === 0 ? close() : setStep(step - 1));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        dailyTarget: Math.max(500, Math.min(10000, parseInt(dailyTarget, 10) || 1500)),
        reminderInterval,
        activeHoursStart: startTime,
        activeHoursEnd: endTime,
        activeDays: activeDays.map((d) => DAY_MAP[d] || d.toLowerCase()),
      };

      await apiRequest('PUT', '/api/profile', payload);
      await queryClient.invalidateQueries({ queryKey: ['/api/profile'] });
      await queryClient.invalidateQueries({ queryKey: ['/api/stats/daily'] });
      toast({
        title: 'Hydration settings saved!',
        description: 'Your changes have been saved.',
      });
      setLocation('/profile');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Update Failed',
        description: getErrorMessage(error),
      });
    } finally {
      setSaving(false);
    }
  };

  const next = () => {
    if (step < totalSteps - 1) setStep(step + 1);
    else save();
  };

  const stepContent = useMemo(() => {
    switch (step) {
      case 0:
        return (
          <ReminderFrequencyStep
            reminderInterval={reminderInterval}
            setReminderInterval={setReminderInterval}
          />
        );
      case 1:
        return (
          <DailyGoalStep
            recommendedGoal={recommended()}
            dailyTarget={dailyTarget}
            setDailyTarget={setDailyTarget}
          />
        );
      case 2:
        return (
          <ActiveHoursDaysStep
            startTime={startTime}
            endTime={endTime}
            setStartTime={setStartTime}
            setEndTime={setEndTime}
            activeDays={activeDays}
            toggleDay={toggleDay}
          />
        );
      default:
        return null;
    }
  }, [step, reminderInterval, dailyTarget, startTime, endTime, activeDays, age, gender]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-lilac-300 border-t-purple-500" />
      </div>
    );
  }

  return (
    <OnboardingShell
      step={step}
      totalSteps={totalSteps}
      variant="bars"
      headerLabel="Hydration Settings"
      onClose={close}
      onBack={back}
      onNext={next}
      nextLabel={step === totalSteps - 1 ? (saving ? 'Saving...' : 'Save Changes') : 'Next'}
      canProceed={!saving}
    >
      {stepContent}
    </OnboardingShell>
  );
}
